/**
 * User-facing formatters for the usage display surfaces (sidebar +
 * per-job summary). Extracted from JobDetailView / UsageSidebar so
 * future surfaces (admin views, exports) consume the same vocabulary
 * and the formatters can be unit-tested as pure functions.
 */

/**
 * Sidebar aggregate duration — "Xh Ym" or "Ym". Rounds to the nearest
 * minute (30s → 1m, 29s → 0m). Used for monthly totals where the
 * "0m" floor is essentially unreachable (> 0 videos with < 30s total
 * is degenerate).
 */
export function formatAggregateDuration(ms: number): string {
  if (ms <= 0) return "0m";
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

/**
 * Per-clip duration shown alongside a single video — "MM:SS" or
 * "H:MM:SS" for clips ≥ 1 hour. Reads more naturally for short rallies
 * than the aggregate "Xh Ym" form.
 */
export function formatVideoDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

/**
 * End-to-end processing time — "Xh Ym Zs", or shorter. Skips leading
 * zero units so a 14-second job shows "14s", not "0m 14s". Includes
 * minutes when hours are present so "1h 0m 5s" makes sense.
 */
export function formatProcessingTime(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || h > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(" ");
}

/**
 * File size in MB or GB. Threshold at 1024 MB (1 GiB) — anything
 * larger reads as "X.YZ GB" with 2 decimals. Sub-10 MB shows one
 * decimal ("3.4 MB"), 10+ MB rounds to integer ("240 MB").
 */
export function formatBytes(bytes: number): string {
  if (bytes < 0) bytes = 0;
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

const BYTES_PER_MB = 1024n * 1024n;
const MB_PER_GB = 1024n;

/**
 * ``formatBytes`` for a decimal-string byte count — the RallyClip v1
 * ``source.byte_length`` wire type (a u64 as a decimal string). Uses
 * BigInt so a multi-GB length is never rounded through ``Number``;
 * returns null for anything that is not a plain non-negative decimal
 * string (the caller then omits the size). Same thresholds and
 * rounding as ``formatBytes``: one decimal below 10 MB, integer MB
 * below 1 GiB, two-decimal GB above.
 */
export function formatDecimalBytes(decimal: string): string | null {
  if (typeof decimal !== "string" || !/^\d+$/.test(decimal)) return null;
  const bytes = BigInt(decimal);
  // Branch on the UNROUNDED value, as formatBytes does (`mb < 10`,
  // `mb < 1024`), then round half-up in integer arithmetic.
  if (bytes < 10n * BYTES_PER_MB) {
    const tenthsMb = (bytes * 10n + BYTES_PER_MB / 2n) / BYTES_PER_MB;
    return `${tenthsMb / 10n}.${tenthsMb % 10n} MB`;
  }
  if (bytes < MB_PER_GB * BYTES_PER_MB) {
    const wholeMb = (bytes + BYTES_PER_MB / 2n) / BYTES_PER_MB;
    return `${wholeMb} MB`;
  }
  const hundredthsGb =
    (bytes * 100n + (BYTES_PER_MB * MB_PER_GB) / 2n) / (BYTES_PER_MB * MB_PER_GB);
  const whole = hundredthsGb / 100n;
  const frac = (hundredthsGb % 100n).toString().padStart(2, "0");
  return `${whole}.${frac} GB`;
}

/**
 * Live-projected ETA copy for the ProgressPanel active sub-state
 * (worker-progress-eta-one-pager.md, C32).
 *
 * Honesty contract:
 * - Returns null when the projection lacks meaningful signal (very
 *   early progress, missing percentage, or nonsense values). The
 *   caller renders "Estimating…" in that case.
 * - Otherwise returns the user-facing hedge phrase: "less than a
 *   minute", "about 2 min remaining", "about 1 h 5 min remaining".
 *
 * Quake-style projection: total = elapsed / fraction → remaining =
 * total − elapsed. Below 5 % overall we hide the result (early
 * projections swing wildly).
 */
export function formatEta(
  elapsedMs: number,
  overallPercentage: number | null | undefined,
): string | null {
  if (
    overallPercentage == null ||
    !Number.isFinite(overallPercentage) ||
    overallPercentage < 0.05 ||
    overallPercentage > 1 ||
    elapsedMs <= 0
  ) {
    return null;
  }
  const remainingMs = elapsedMs / overallPercentage - elapsedMs;
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    return null;
  }
  const totalSec = Math.round(remainingMs / 1000);
  if (totalSec < 60) return "less than a minute";
  const totalMin = Math.round(totalSec / 60);
  if (totalMin < 60) return `about ${totalMin} min remaining`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (m === 0) return `about ${h} h remaining`;
  return `about ${h} h ${m} min remaining`;
}
